import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

const STATE_FAMILY = { Missing: "red", Pending: "amber", Approved: "green", Rejected: "red" };
const STATES = ["Missing", "Pending", "Approved", "Rejected"];

export default function TimesheetStatus() {
  const { data, isLoading } = useQuery({ queryKey: ["ts-status"], queryFn: () => get("/timesheets/status") });
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.employees.filter((e) => {
      if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (stateFilter !== "all" && e.state !== stateFilter) return false;
      return true;
    });
  }, [data, search, stateFilter]);

  if (isLoading || !data) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Timesheet Status" subtitle={`Who has submitted for the week of ${new Date(data.week_start).toLocaleDateString()}.`} />
      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="ts-status-search" placeholder="Search employee name" value={search} onChange={setSearch} />
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-44" data-testid="ts-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {data.employees.length === 0 ? (
        <EmptyState icon={Search} title="No data yet" subtitle="No timesheet status available for this week." />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="No matches" subtitle="No employees match your search or filter." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="timesheet-status-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Employee</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Hours Logged</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.employee_id} data-testid={`ts-status-row-${e.employee_id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar name={e.name} size="sm" color="#64748B" /><span className="font-medium">{e.name}</span></div></td>
                  <td className="px-4 py-3"><StatusBadge family={STATE_FAMILY[e.state]} label={e.state} /></td>
                  <td className="px-4 py-3 text-right font-mono">{e.total}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
